import React, { useState } from 'react';
import Form from '@dashboard/components/Formik/Form';
import SettingsHeader from '@dashboard/components/SettingsHeader';
import SettingsContainer from '@/dashboard/components/SettingsContainer';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import useSellerQueries from '@dashboard/hooks/queries/useSellerQueries';
import { TextInput } from '@dashboard/components/Formik';
import SettingsDivider from '@dashboard/components/SettingsDivider';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetcher } from '@dashboard/services/request';
import TabLink from '@dashboard/components/TabLink';
import { rootDomain } from '@dashboard/services/env';
import { InlineSnippet } from '@dashboard/components/InlineSnippet';
import Spinner from '@dashboard/components/Spinner/Spinner';
import * as Yup from 'yup';
import {
  InformationCircleIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import { Alert, AlertTitle, AlertDescription } from '@dashboard/components/ui/alert';
import Features from '@dashboard/enums/features';
import CanUseFeature from '@dashboard/components/CanUseFeature';
import { isEmpty } from 'lodash';
import { domainStatusKey, pagesKey } from '@dashboard/lib/queryKeys';
import SettingsDescription from '@dashboard/components/SettingsDescription';
import PageHeader from '@dashboard/components/PageHeader';
import hasLinkInBioLab from '@dashboard/lib/linkInBioLab';
import { getLinkInBioShortUrl } from '@dashboard/services/getLinkInBioUrl';

const SettingsDomains = () => {
  const { seller, setSeller } = useAuth();
  const { updateSeller } = useSellerQueries();
  const queryClient = useQueryClient();

  return (
    <>
      <div className="p-4 space-y-4">
        <PageHeader
          title="Domain settings"
          description="Host your pages and customer portal on your custom domain or on your Checkout Page subdomain."
        />

        <div>
          <SettingsContainer size="lg">
            <Alert variant="default" className="my-4">
              <InformationCircleIcon />
              <AlertTitle>Note</AlertTitle>
              <AlertDescription>
                If you change your URL, your old URL will no longer work, and you will need to
                update your links
                {hasLinkInBioLab(seller) ? ', including your link in bio and its QR code.' : '.'}
              </AlertDescription>
            </Alert>

            {!isEmpty(seller) && (
              <>
                <Form
                  formSubmitAlign="end"
                  initialValues={{
                    name: seller.name,
                  }}
                  onSubmit={async (values, actions) => {
                    const { data, error } = await updateSeller(values);

                    if (data) {
                      actions.resetForm({
                        values: {
                          name: data.name,
                        },
                      });
                    }

                    return {
                      data,
                      error,
                      onSuccess: () => queryClient.invalidateQueries({ queryKey: pagesKey }),
                    };
                  }}
                  validationSchema={Yup.object().shape({
                    name: Yup.string().required('Name is required').min(3).max(50),
                  })}
                  fields={[
                    {
                      name: 'name',
                      required: true,
                      type: 'custom',
                      renderField: (field, { values }) => (
                        <div className="w-full space-y-1.5">
                          <div className="flex w-full">
                            <TextInput
                              {...field}
                              required
                              className="rounded-r-none w-full relative focus-visible:z-10"
                            />
                            <div className="bg-muted h-8 w-2/3 flex items-center rounded-r-lg border border-input border-l-0 px-2.5 py-1 text-sm text-foreground">
                              .{rootDomain}
                            </div>
                          </div>
                          {hasLinkInBioLab(seller) && values.name && (
                            <p className="text-sm text-muted-foreground">
                              Also your link in bio:{' '}
                              {getLinkInBioShortUrl({ name: values.name }).replace(
                                /^https?:\/\//,
                                '',
                              )}
                            </p>
                          )}
                        </div>
                      ),
                    },
                  ]}
                />
              </>
            )}
          </SettingsContainer>

          <SettingsContainer size="lg">
            <SettingsDivider />
          </SettingsContainer>

          <SettingsContainer size="lg">
            <CanUseFeature feature={Features.CUSTOM_DOMAIN}>
              <SettingsHeader id="custom-domain">Custom domain</SettingsHeader>

              <SettingsDescription className="mb-4">
                Add your own (sub)domain to your pages and customer portal.
              </SettingsDescription>

              <Alert variant="default" className="my-4">
                <InformationCircleIcon />
                <AlertTitle>Note</AlertTitle>
                <AlertDescription>
                  <p>
                    If your domain is <InlineSnippet>example.com</InlineSnippet>, we recommend using{' '}
                    <InlineSnippet>buy.example.com</InlineSnippet> as your custom subdomain. You can
                    replace <InlineSnippet>buy</InlineSnippet> with anything you like.
                  </p>
                </AlertDescription>
              </Alert>

              {!isEmpty(seller) && (
                <>
                  <Form
                    formSubmitAlign="end"
                    initialValues={{
                      customDomain: seller.customDomain,
                    }}
                    onSubmit={async (values, actions) => {
                      const { data, error } = await updateSeller(values);

                      if (data) {
                        actions.resetForm({
                          values: {
                            customDomain: data.customDomain,
                          },
                        });
                      }

                      return {
                        data,
                        error,
                        onSuccess: () => {
                          setSeller(data);
                          queryClient.invalidateQueries({ queryKey: pagesKey });
                        },
                      };
                    }}
                    fields={[
                      {
                        name: 'customDomain',
                        type: 'text',
                        label: 'Custom domain',
                        placeholder: 'buy.example.com',
                      },
                    ]}
                  />

                  <DomainConfiguration domain={seller.customDomain} />
                </>
              )}
            </CanUseFeature>
          </SettingsContainer>
        </div>
      </div>
    </>
  );
};

function useDomainStatus({ domain }) {
  const { token, sellerId } = useAuth();

  const { data, isRefetching, isLoading, refetch } = useQuery({
    queryKey: domainStatusKey(domain),
    queryFn: () => fetcher(`/domains/${domain}/verify?sellerId=${sellerId}`, token),
    enabled: !!domain,
    retryOnMount: true,
    refetchInterval: 5000,
  });

  return {
    status: data?.status,
    domainJson: data?.domain,
    refetch,
    isLoading: isRefetching || isLoading,
  };
}

const getSubdomain = (name, apexName) => {
  if (name === apexName) return null;
  return name.slice(0, name.length - apexName.length - 1);
};

function DomainConfiguration({ domain }) {
  const [recordType, setRecordType] = useState('CNAME');

  const { status, domainJson, isLoading } = useDomainStatus({
    domain,
  });

  if (!domain) return null;

  if (!status || !domainJson) {
    if (isLoading) {
      return (
        <div className="flex justify-end items-center text-gray-500 dark:text-muted-foreground mt-4">
          <Spinner size="small" />
          <span className="ml-2">Checking domain status</span>
        </div>
      );
    }

    return null;
  }

  const subdomain = getSubdomain(domainJson.name, domainJson.apexName);

  const txtVerification =
    (status === 'Pending Verification' && domainJson.verification?.find(x => x.type === 'TXT')) ||
    null;

  return (
    <div className="mt-4">
      {status !== 'Valid Configuration' && <h3>Create DNS records</h3>}

      {txtVerification ? (
        <div className="mb-4 text-left">
          <p className="mb-4">
            Add the following TXT record on <InlineSnippet>{domainJson.apexName}</InlineSnippet> to
            prove ownership of <InlineSnippet>{domainJson.name}</InlineSnippet>:
          </p>

          <div className="flex items-start justify-start space-x-10 rounded-md bg-gray-100 dark:bg-muted p-2">
            <div>
              <p className="text-sm font-bold">Type</p>
              <p className="mt-2 font-mono text-sm">{txtVerification.type}</p>
            </div>
            <div>
              <p className="text-sm font-bold">Name</p>
              <p className="mt-2 font-mono text-sm">
                {txtVerification.domain.slice(
                  0,
                  txtVerification.domain.length - domainJson.apexName.length - 1,
                )}
              </p>
            </div>
            <div>
              <p className="text-sm font-bold">Value</p>
              <p className="mt-2 font-mono text-sm">
                <span className="text-ellipsis wrap-anywhere">{txtVerification.value}</span>
              </p>
            </div>
          </div>

          <p className="mt-4 text-sm">
            Warning: if you are using this domain for another site, adding this TXT record will
            transfer domain ownership away from that site and break it. Exercise caution when
            setting this record.
          </p>
        </div>
      ) : status === 'Unknown Error' ? (
        <p>{domainJson.error.message}</p>
      ) : null}

      {status === 'Pending Verification' && (
        <>
          <div className="flex justify-start space-x-4">
            {subdomain ? (
              <>
                <button type="button" onClick={() => setRecordType('CNAME')}>
                  <TabLink selected={recordType === 'CNAME'}>CNAME Record (recommended)</TabLink>
                </button>

                <button type="button" onClick={() => setRecordType('A')}>
                  <TabLink selected={recordType === 'A'}>A Record</TabLink>
                </button>
              </>
            ) : (
              <>
                <button type="button" onClick={() => setRecordType('A')}>
                  <TabLink selected={recordType === 'A'}>A Record (recommended)</TabLink>
                </button>

                <button type="button" onClick={() => setRecordType('CNAME')}>
                  <TabLink selected={recordType === 'CNAME'}>CNAME Record</TabLink>
                </button>
              </>
            )}
          </div>

          <div className="my-4 text-left">
            <p className="my-4">
              To configure your {recordType === 'A' ? 'apex domain' : 'subdomain'} (
              <InlineSnippet>
                {recordType === 'A' ? domainJson.apexName : domainJson.name}
              </InlineSnippet>
              ), add the following {recordType} record on your DNS provider to continue:
            </p>
            <div className="flex items-start justify-start space-x-10 rounded bg-gray-100 dark:bg-muted p-2">
              <div>
                <p className="text-sm font-bold">Type</p>
                <p className="mt-2 font-mono text-sm">{recordType}</p>
              </div>
              <div>
                <p className="text-sm font-bold">Name</p>
                <p className="mt-2 font-mono text-sm">
                  {recordType === 'A' ? '@' : (subdomain ?? 'www')}
                </p>
              </div>
              <div>
                <p className="text-sm font-bold">Value</p>
                <p className="mt-2 font-mono text-sm wrap-anywhere">
                  {recordType === 'A' ? `76.76.21.21` : `cname.${rootDomain}`}
                </p>
              </div>
            </div>
          </div>
        </>
      )}

      <div className="mb-4">
        {status === 'Pending Verification' ? (
          <Alert variant="warning" className="my-4">
            <ExclamationCircleIcon />
            <AlertTitle>{status}</AlertTitle>
            <AlertDescription>
              If you just configured your domain, it may take some time until we can verify it. We
              will check your domain status every 5 seconds.
            </AlertDescription>
          </Alert>
        ) : status === 'Valid Configuration' ? (
          <Alert variant="success" className="my-4">
            <CheckCircleIcon />
            <AlertTitle>{status}</AlertTitle>
            <AlertDescription>
              If you just configured your domain, it may take up to an hour until your new checkout
              links work.
            </AlertDescription>
          </Alert>
        ) : (
          <Alert variant="destructive" className="my-4">
            <ExclamationCircleIcon />
            <AlertTitle>{status}</AlertTitle>
          </Alert>
        )}
      </div>

      {isLoading && (
        <div className="flex justify-end items-center text-gray-500 dark:text-muted-foreground mt-4">
          <Spinner size="small" />
          <span className="ml-2">Checking domain status</span>
        </div>
      )}
    </div>
  );
}

export default SettingsDomains;
